import { createElement, replaceChildren } from "../create-element";
import { isDefined, Option, safeExecute, Terminator } from "@opendaw/lib-std";
import { RouteLocation, RouteMatcher } from "../routes";
export const Router = ({ runtime, service, routes, fallback, preloader, error, onshow }) => {
    const routing = RouteMatcher.create(routes);
    const container = createElement("div", { style: { display: "contents" } });
    let loading = Option.None;
    let showing = Option.None;
    const fetchPage = async (pageFactory, path) => {
        if (loading.nonEmpty()) {
            const request = loading.unwrap();
            request.preloader.ifSome(lifecycle => lifecycle.terminate());
            request.state = "cancelled";
            // Tear the cancelled page down NOW, before its replacement's factory runs. Its factory has already
            // executed (side effects like screen subscriptions and singleton panel bindings are live), and the
            // replacement is created synchronously below; without this a reused singleton (the workspace panels)
            // would be bound twice while this page still holds it. Deferring to the awaited cleanup below is too
            // late. Terminate is idempotent, so the later `cancelled` cleanup is a harmless no-op.
            request.lifecycle.terminate();
            loading = Option.None;
        }
        const lifecycle = new Terminator();
        const pageResult = pageFactory({
            service,
            lifecycle,
            path,
            error: ""
        });
        const content = pageResult instanceof Promise ? pageResult : Promise.resolve(pageResult);
        const request = {
            path,
            content,
            lifecycle,
            factory: pageFactory,
            preloader: Option.wrap(safeExecute(preloader)),
            state: "loading"
        };
        loading = Option.wrap(request);
        let element;
        try {
            element = await content;
        }
        catch (reason) {
            console.warn(reason);
            if (isDefined(error)) {
                return fetchPage(error, path);
            }
            else {
                alert(`Could not load page (${reason})`);
            }
        }
        if (request.state === "cancelled") {
            request.lifecycle.terminate();
        }
        else if (request.path === path) {
            if (showing.nonEmpty()) {
                showing.unwrap().lifecycle.terminate();
                showing = Option.None;
            }
            if (loading.nonEmpty()) {
                loading.unwrap().preloader.ifSome(lifecycle => lifecycle.terminate());
                loading = Option.None;
            }
            replaceChildren(container, element);
            showing = Option.wrap(request);
            safeExecute(onshow);
        }
    };
    runtime.own(RouteLocation.get()
        .catchupAndSubscribe((location) => {
        if (showing.unwrapOrNull()?.path === location.path) {
            return;
        }
        const route = routing.resolve(location.path);
        const factory = route.mapOr(({ factory }) => factory, () => fallback);
        const current = showing.unwrapOrNull();
        if (isDefined(current) && current.factory === factory && route.mapOr(({ reuse }) => reuse === true, false)) {
            current.path = location.path;
            return;
        }
        return fetchPage(factory, location.path);
    }));
    return container;
};
